import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import axios from "axios";
import { COLORS } from "../../utils/colors";
import { api } from "../../utils/api";
import { showErrorToast } from "../../utils/toast";
import { getCurrentP1Region } from "../../utils/p1Region";

// Module-level cache and request management
let cachedCustomers = null;
let cacheTimestamp = null;
let activeRequestPromise = null;
const CACHE_DURATION = 30000; // 30 seconds

// Get API domain (same logic as api utility)
const getApiDomain = () => {
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const hostname = window.location.hostname;
  const isNzRegion = getCurrentP1Region() === "NZ";

  if (isLocalhost) {
    return "http://localhost:8095/";
  } else if (isNzRegion) {
    return "https://api.priorityonewashclub.co.nz/";
  } else if (
    hostname.includes("staging.dm6wsofv5048b.amplifyapp.com") ||
    hostname.includes("dm6wsofv5048b.amplifyapp.com") ||
    hostname.includes("p1-staging") ||
    !hostname.includes("priorityonewashclub.com")
  ) {
    return "https://p1-staging-api.priorityonewashclub.com/";
  } else {
    return "https://api.priorityonewashclub.com/";
  }
};

const UpdateSubscriptionPrices = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [selectionOrder, setSelectionOrder] = useState([]); // preserves selection order
  const [excludedSubscriptions, setExcludedSubscriptions] = useState(new Set());
  const [pricingMode, setPricingMode] = useState("set"); // 'set' or 'adjust'
  const [priceValue, setPriceValue] = useState("");
  const [loading, setLoading] = useState(true); // true so loading shows before list/message
  const [updating, setUpdating] = useState(false);
  const [updateResults, setUpdateResults] = useState(null);
  const [error, setError] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [dryRunResults, setDryRunResults] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [currentPrices, setCurrentPrices] = useState(new Map()); // Map of customerId -> { subscriptionId -> currentPrice }
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [customersWithoutStripe, setCustomersWithoutStripe] = useState(new Set()); // Track customers without Stripe customer
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Check cache first
    if (cachedCustomers && cacheTimestamp && Date.now() - cacheTimestamp < CACHE_DURATION) {
      setCustomers(cachedCustomers);
      setLoading(false);
      return;
    }

    // If there's an active request, wait for it
    if (activeRequestPromise) {
      activeRequestPromise
        .then((response) => {
          if (mountedRef.current && response && response.data && response.data.success) {
            const customersData = response.data.data.customers || [];
            setCustomers(customersData);
            setLoading(false);
          }
        })
        .catch((err) => {
          if (mountedRef.current) {
            setLoading(false);
            if (err.name !== "AbortError" && err.code !== "ERR_CANCELED") {
              const errorMessage =
                err.response?.data?.message ||
                err.response?.data?.data?.error ||
                err.message ||
                "Error loading customers.";
              setError(errorMessage);
            }
          }
        });
      return;
    }

    // Start new request using axios directly (no abort signal)
    fetchCustomers();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchCustomers = async () => {
    const token = sessionStorage.getItem("token") || "";
    if (!token) {
      setLoading(false);
      setError("Missing login token. Please re-login to make updates.");
      return;
    }

    setLoading(true);
    setError(null);

    const domain = getApiDomain();
    const selectedPortal = sessionStorage.getItem("selectedPortal");

    const headers = {
      authorization: `Bearer ${token}`
    };

    if (selectedPortal) {
      headers["x-selected-portal"] = selectedPortal;
    }

    // Create axios request WITHOUT abort signal - this is the key fix
    const axiosRequest = axios({
      method: "GET",
      url: domain + "v1/subscription/selfsignup-customers",
      headers,
      timeout: 120000, // Increased to 2 minutes for large datasets
      validateStatus: function (status) {
        return status >= 200 && status < 600;
      }
    });

    // Store promise globally
    activeRequestPromise = axiosRequest;

    try {
      const response = await axiosRequest;

      if (!mountedRef.current) {
        return;
      }

      if (response && response.data && response.data.success) {
        const customersData = response.data.data.customers || [];
        setCustomers(customersData);
        cachedCustomers = customersData;
        cacheTimestamp = Date.now();
      } else if (response?.status === 401) {
        setError("Missing login token. Please re-login to make updates.");
      } else {
        const errorMessage = response?.data?.message || response?.data?.data?.error || "Failed to fetch customers";
        setError(errorMessage);
        console.error("API response error:", response?.data);
      }
    } catch (err) {
      if (!mountedRef.current) {
        return;
      }

      if (err.name === "AbortError" || err.code === "ERR_CANCELED" || err.message?.includes("canceled")) {
        console.log("Request was canceled");
        return;
      }
      console.error("Error fetching customers:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.data?.error ||
        err.message ||
        "Error loading customers. Please try again.";
      setError(errorMessage);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
      // Clear after delay
      setTimeout(() => {
        activeRequestPromise = null;
      }, 1000);
    }
  };

  // Fetch current prices when customers are selected
  const fetchCurrentPricesForCustomers = async (customerIds) => {
    if (customerIds.length === 0) {
      setCurrentPrices(new Map());
      return;
    }

    setLoadingPrices(true);
    try {
      const response = await api("v1/subscription/current-prices", "POST", {
        customerIds
      });

      if (response.data.success) {
        // Merge with existing prices so previously fetched customers are retained
        const pricesMap = new Map(currentPrices);
        response.data.data.customers.forEach((customer) => {
          const subscriptionPrices = new Map();
          customer.subscriptions.forEach((sub) => {
            subscriptionPrices.set(sub.subscriptionId.toString(), {
              currentPrice: sub.currentPrice / 100, // Convert cents to dollars
              currency: sub.currency || "aud",
              status: sub.status,
              stripeSubscriptionId: sub.stripeSubscriptionId,
              found: sub.found !== false // Default to true if not specified
            });
          });
          pricesMap.set(customer.customerId.toString(), subscriptionPrices);
        });
        setCurrentPrices(pricesMap);
      }
    } catch (err) {
      console.error("Error fetching current prices:", err);
      setError("Failed to fetch current prices. Please try again.");
    } finally {
      setLoadingPrices(false);
    }
  };

  const handleSelectCustomer = async (customerId) => {
    const newSelected = new Set(selectedCustomers);
    const customerIdStr = customerId.toString();
    if (newSelected.has(customerId)) {
      newSelected.delete(customerId);
      // Remove prices for deselected customer
      const newPrices = new Map(currentPrices);
      newPrices.delete(customerIdStr);
      setCurrentPrices(newPrices);
      setSelectionOrder(selectionOrder.filter((id) => id !== customerIdStr));
      // Remove from customersWithoutStripe tracking
      const newWithoutStripe = new Set(customersWithoutStripe);
      newWithoutStripe.delete(customerIdStr);
      setCustomersWithoutStripe(newWithoutStripe);
      const customer = customers.find((c) => c.customerId === customerId);
      if (customer) {
        const newExcluded = new Set(excludedSubscriptions);
        customer.subscriptions.forEach((sub) => newExcluded.delete(sub.subscriptionId?.toString()));
        setExcludedSubscriptions(newExcluded);
      }
      setSelectedCustomers(newSelected);
    } else {
      newSelected.add(customerId);
      setSelectedCustomers(newSelected);
      setSelectionOrder([...selectionOrder, customerIdStr]);

      // Remove from customersWithoutStripe if they're being reselected (in case backfill succeeds)
      const newWithoutStripe = new Set(customersWithoutStripe);
      newWithoutStripe.delete(customerIdStr);
      setCustomersWithoutStripe(newWithoutStripe);

      // First, backfill any missing Stripe IDs for this customer
      try {
        const backfillResponse = await api("v1/subscription/backfill-stripe-ids", "POST", {
          customerIds: [customerId]
        });

        // Track customers without Stripe customer
        if (backfillResponse.data.success && backfillResponse.data.data.customersWithoutStripeCustomer) {
          const withoutStripe = backfillResponse.data.data.customersWithoutStripeCustomer;
          const updatedWithoutStripe = new Set(customersWithoutStripe);
          withoutStripe.forEach((c) => {
            if (c.customerId === customerIdStr) {
              updatedWithoutStripe.add(customerIdStr);
            }
          });
          setCustomersWithoutStripe(updatedWithoutStripe);
        }
      } catch (err) {
        console.error("Error backfilling Stripe IDs:", err);
        // Continue anyway - we'll still try to fetch prices
      }

      // Then fetch prices for newly selected customer
      await fetchCurrentPricesForCustomers([customerId]);
      await validateSelectionAndDeselectInvalid([customerId]);
    }
  };

  const validateSelectionAndDeselectInvalid = async (ids) => {
    if (ids.length === 0) return;
    try {
      const response = await api("v1/subscription/validate-stripe-subscriptions", "POST", {
        customerIds: ids.map((id) => (typeof id === "string" ? id : id.toString()))
      });
      if (!response.data?.success || !response.data?.data?.invalid?.length) return;
      const invalid = response.data.data.invalid;
      const invalidIds = new Set(invalid.map((i) => i.customerId));
      const validIds = ids.filter((id) => !invalidIds.has(id.toString()));
      setSelectedCustomers(new Set(validIds));
      setSelectionOrder(validIds.map((id) => id.toString()));
      setCurrentPrices((prev) => {
        const next = new Map(prev);
        invalid.forEach((i) => next.delete(i.customerId));
        return next;
      });
      const emails = invalid.map((i) => i.email).join(", ");
      showErrorToast(`The following do not have a valid Stripe subscription and were deselected: ${emails}`);
    } catch (err) {
      console.error("Error validating Stripe subscriptions:", err);
    }
  };

  const handleToggleSubscription = (subscriptionId) => {
    const subIdStr = subscriptionId.toString();
    const newExcluded = new Set(excludedSubscriptions);
    if (newExcluded.has(subIdStr)) {
      newExcluded.delete(subIdStr);
    } else {
      newExcluded.add(subIdStr);
    }
    setExcludedSubscriptions(newExcluded);
  };

  const buildUpdatePayload = () => {
    const parsedAmount = parseFloat(priceValue);
    const currentIsSetMode = pricingMode === "set";

    const allCustomerIds = Array.from(selectedCustomers);
    const subscriptionIds = [];
    const customerIds = [];
    allCustomerIds.forEach((cId) => {
      const customer = customers.find((c) => c.customerId === cId);
      if (customer) {
        const includedSubs = customer.subscriptions.filter(
          (sub) => !excludedSubscriptions.has(sub.subscriptionId?.toString())
        );
        if (includedSubs.length > 0) {
          customerIds.push(cId);
          includedSubs.forEach((sub) => subscriptionIds.push(sub.subscriptionId));
        }
      }
    });

    const payload = { customerIds, subscriptionIds };
    if (currentIsSetMode) {
      payload.setPrice = parsedAmount;
    } else {
      payload.adjustment = parsedAmount;
    }
    return payload;
  };

  const handleUpdatePrices = async () => {
    const parsedAmount = parseFloat(priceValue);
    const currentIsSetMode = pricingMode === "set";

    if (Number.isNaN(parsedAmount)) {
      setError(currentIsSetMode ? "Please enter a price" : "Please enter an adjustment amount");
      return;
    }

    if (currentIsSetMode && parsedAmount <= 0) {
      setError("Please enter a price greater than 0");
      return;
    }

    if (!currentIsSetMode && parsedAmount === 0) {
      setError("Adjustment must be non-zero");
      return;
    }

    if (selectedCustomers.size === 0) {
      setError("Please select at least one customer");
      return;
    }

    if (totalSelectedSubscriptions === 0) {
      setError("All subscriptions are excluded. Please include at least one subscription.");
      return;
    }

    setDryRunLoading(true);
    setDryRunResults(null);
    setError(null);

    try {
      const payload = buildUpdatePayload();
      payload.dryRun = true;

      const response = await api("v1/subscription/update-prices", "POST", payload);

      if (response.data.success) {
        setDryRunResults(response.data.data);
        setShowConfirmDialog(true);
      } else {
        setError(response.data.message || "Failed to preview price changes");
      }
    } catch (err) {
      console.error("Error running dry run:", err);
      setError(
        err.response?.data?.data?.error ||
          err.response?.data?.message ||
          "Error previewing price changes. Please try again."
      );
    } finally {
      setDryRunLoading(false);
    }
  };

  const confirmUpdate = async () => {
    setShowConfirmDialog(false);
    setDryRunResults(null);
    setUpdating(true);
    setError(null);

    try {
      const payload = buildUpdatePayload();

      const response = await api("v1/subscription/update-prices", "POST", payload);

      if (response.data.success) {
        setUpdateResults(response.data.data);
        setSelectedCustomers(new Set());
        setExcludedSubscriptions(new Set());
        setPriceValue("");
        cachedCustomers = null;
        cacheTimestamp = null;
        activeRequestPromise = null;
        await fetchCustomers();
      } else {
        setError(response.data.message || "Failed to update prices");
      }
    } catch (err) {
      console.error("Error updating prices:", err);
      setError(
        err.response?.data?.data?.error || err.response?.data?.message || "Error updating prices. Please try again."
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleBack = () => {
    setUpdateResults(null);
    setDryRunResults(null);
    setSelectedCustomers(new Set());
    setExcludedSubscriptions(new Set());
    setPriceValue("");
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (updateResults) {
    return (
      <Box sx={{ padding: 3 }}>
        <Typography variant="h4" gutterBottom>
          Update Results
        </Typography>

        <Alert severity="success" sx={{ mb: 3 }}>
          Update completed! {updateResults.summary.successCount} subscription(s) updated successfully.
          {updateResults.summary.errorCount > 0 && ` ${updateResults.summary.errorCount} error(s) occurred.`}
        </Alert>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Typography>Total Customers: {updateResults.summary.totalCustomers}</Typography>
            <Typography>Successfully Updated: {updateResults.summary.successCount}</Typography>
            <Typography>Errors: {updateResults.summary.errorCount}</Typography>
          </CardContent>
        </Card>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Old Price</TableCell>
                <TableCell>New Price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {updateResults.results.map((result) =>
                result.subscriptions.map((sub, idx) => (
                  <TableRow key={`${result.customerId}-${idx}`}>
                    <TableCell>
                      {result.firstName} {result.lastName}
                    </TableCell>
                    <TableCell>{result.email}</TableCell>
                    <TableCell>{sub.stripeSubscriptionId}</TableCell>
                    <TableCell>
                      <Typography color={sub.status === "success" ? "success.main" : "error.main"}>
                        {sub.status}
                      </Typography>
                    </TableCell>
                    <TableCell>${sub.oldPrice?.toFixed(2) || "N/A"}</TableCell>
                    <TableCell>${sub.newPrice?.toFixed(2) || "N/A"}</TableCell>
                    {sub.error && (
                      <TableCell>
                        <Typography variant="caption" color="error">
                          {sub.error}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Button variant="contained" onClick={handleBack} sx={{ mt: 3, backgroundColor: COLORS.priorityBlue.hex }}>
          Back to Customer List
        </Button>
      </Box>
    );
  }

  const filteredCustomers = customers.filter((customer) => {
    if (!filterText) return true;
    const searchText = filterText.toLowerCase();
    return (
      customer.firstName?.toLowerCase().includes(searchText) ||
      customer.lastName?.toLowerCase().includes(searchText) ||
      customer.email?.toLowerCase().includes(searchText) ||
      customer.phone?.toLowerCase().includes(searchText)
    );
  });

  const selectedCustomersList = selectionOrder
    .map((id) => customers.find((c) => c.customerId.toString() === id))
    .filter(Boolean);
  const parsedPriceValue = parseFloat(priceValue);
  const isSetMode = pricingMode === "set";
  const isPriceValid =
    !Number.isNaN(parsedPriceValue) && ((isSetMode && parsedPriceValue > 0) || (!isSetMode && parsedPriceValue !== 0));
  const totalSelectedSubscriptions = selectedCustomersList.reduce(
    (sum, c) =>
      sum + c.subscriptions.filter((sub) => !excludedSubscriptions.has(sub.subscriptionId?.toString())).length,
    0
  );
  const effectiveCustomerCount = selectedCustomersList.filter((c) =>
    c.subscriptions.some((sub) => !excludedSubscriptions.has(sub.subscriptionId?.toString()))
  ).length;

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" gutterBottom>
        Update Subscription Prices
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Select customers with self signup and valid Stripe subscriptions, then set the new monthly price.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Customer Selection Table */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2
          }}
        >
          <Typography variant="h6">
            Customers ({customers.length} total, {selectedCustomers.size} selected)
          </Typography>
          <TextField
            label="Filter customers"
            variant="outlined"
            size="small"
            value={filterText}
            onChange={(e) => {
              setFilterText(e.target.value);
              setPage(0);
            }}
            sx={{ width: 250 }}
          />
        </Box>

        {/* Filter customers */}
        {(() => {
          const paginatedCustomers = filteredCustomers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

          return (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={
                            paginatedCustomers.length > 0 &&
                            paginatedCustomers.every((c) => selectedCustomers.has(c.customerId))
                          }
                          indeterminate={
                            paginatedCustomers.some((c) => selectedCustomers.has(c.customerId)) &&
                            !paginatedCustomers.every((c) => selectedCustomers.has(c.customerId))
                          }
                          onChange={async (e) => {
                            if (e.target.checked) {
                              const newSelected = new Set(selectedCustomers);
                              const newlySelectedIds = paginatedCustomers
                                .filter((c) => !selectedCustomers.has(c.customerId))
                                .map((c) => c.customerId);

                              paginatedCustomers.forEach((c) => {
                                newSelected.add(c.customerId);
                                if (!selectionOrder.includes(c.customerId.toString())) {
                                  setSelectionOrder([...selectionOrder, c.customerId.toString()]);
                                }
                              });
                              setSelectedCustomers(newSelected);

                              // Backfill Stripe IDs for newly selected customers
                              if (newlySelectedIds.length > 0) {
                                try {
                                  const backfillResponse = await api("v1/subscription/backfill-stripe-ids", "POST", {
                                    customerIds: newlySelectedIds
                                  });

                                  // Track customers without Stripe customer
                                  if (
                                    backfillResponse.data.success &&
                                    backfillResponse.data.data.customersWithoutStripeCustomer
                                  ) {
                                    const withoutStripe = backfillResponse.data.data.customersWithoutStripeCustomer;
                                    const updatedWithoutStripe = new Set(customersWithoutStripe);
                                    withoutStripe.forEach((c) => {
                                      updatedWithoutStripe.add(c.customerId);
                                    });
                                    setCustomersWithoutStripe(updatedWithoutStripe);
                                  }
                                } catch (err) {
                                  console.error("Error backfilling Stripe IDs:", err);
                                }
                              }

                              fetchCurrentPricesForCustomers(Array.from(newSelected));
                            } else {
                              const newSelected = new Set(selectedCustomers);
                              paginatedCustomers.forEach((c) => {
                                newSelected.delete(c.customerId);
                                const newPrices = new Map(currentPrices);
                                newPrices.delete(c.customerId);
                                setCurrentPrices(newPrices);
                                setSelectionOrder(selectionOrder.filter((id) => id !== c.customerId.toString()));
                              });
                              setSelectedCustomers(newSelected);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Subscriptions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 2, gap: 1 }}>
                            <CircularProgress size={20} />
                            <Typography color="text.secondary">Loading customers...</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {paginatedCustomers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Box sx={{ py: 2 }}>
                            <Typography color="text.secondary">
                              {filterText ? "No customers match your filter" : "No customers found"}
                            </Typography>
                            {!filterText && customers.length === 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Use &quot;Refresh list&quot; to fetch again. Ensure the backend is running (and
                                restarted after the latest code) and has customers with active subscriptions.
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedCustomers.map((customer) => {
                        const isSelected = selectedCustomers.has(customer.customerId);
                        const totalSubscriptions = customer.subscriptions.length;

                        return (
                          <TableRow
                            key={customer.customerId}
                            sx={{
                              backgroundColor: isSelected ? "action.selected" : "inherit",
                              "&:hover": { backgroundColor: "action.hover", cursor: "pointer" }
                            }}
                            onClick={() => handleSelectCustomer(customer.customerId)}
                          >
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleSelectCustomer(customer.customerId)}
                              />
                            </TableCell>
                            <TableCell>
                              {customer.firstName} {customer.lastName}
                            </TableCell>
                            <TableCell>{customer.email}</TableCell>
                            <TableCell>{customer.phone || "N/A"}</TableCell>
                            <TableCell>
                              {totalSubscriptions}
                              {loadingPrices && isSelected && <CircularProgress size={14} sx={{ ml: 1 }} />}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredCustomers.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          );
        })()}
      </Box>

      {/* Price Input */}
      <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant={isSetMode ? "contained" : "outlined"} size="small" onClick={() => setPricingMode("set")}>
            Set exact price
          </Button>
          <Button variant={!isSetMode ? "contained" : "outlined"} size="small" onClick={() => setPricingMode("adjust")}>
            Adjust by amount
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            label={isSetMode ? "New Monthly Price ($)" : "Adjustment Amount ($)"}
            type="number"
            value={priceValue}
            onChange={(e) => setPriceValue(e.target.value)}
            inputProps={{ step: 0.01 }}
            sx={{ width: 250 }}
            helperText={
              isSetMode
                ? "Sets all selected subscriptions to this price"
                : "Enter positive to increase, negative to decrease (e.g., 10 or -5)"
            }
          />
          <Button
            variant="contained"
            onClick={handleUpdatePrices}
            disabled={!isPriceValid || totalSelectedSubscriptions === 0 || updating || dryRunLoading}
            sx={{ backgroundColor: COLORS.priorityBlue.hex }}
          >
            {dryRunLoading ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> Previewing...
              </>
            ) : updating ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Update Prices"
            )}
          </Button>
        </Box>
        {selectedCustomers.size > 0 && isPriceValid && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {isSetMode ? (
              <>
                Update <strong>{effectiveCustomerCount}</strong> customer(s) (
                <strong>{totalSelectedSubscriptions}</strong> subscription(s)) to{" "}
                <strong>${parsedPriceValue.toFixed(2)}/month</strong>
              </>
            ) : (
              <>
                Adjust <strong>{effectiveCustomerCount}</strong> customer(s) (
                <strong>{totalSelectedSubscriptions}</strong> subscription(s)) by{" "}
                <strong>
                  {parsedPriceValue >= 0 ? "+" : ""}${parsedPriceValue.toFixed(2)}/month
                </strong>
              </>
            )}
          </Alert>
        )}
      </Box>

      {/* Selected Customers Table with Subscription Details */}
      {selectedCustomersList.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Selected Customers - Price Preview
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Include</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Subscription ID</TableCell>
                  <TableCell>Current Price</TableCell>
                  <TableCell>New Price</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedCustomersList.map((customer) => {
                  const customerIdStr = customer.customerId?.toString() || customer.customerId;
                  const customerPrices = currentPrices.get(customerIdStr) || new Map();
                  const hasNoStripeCustomer = customersWithoutStripe.has(customerIdStr);

                  return customer.subscriptions.map((sub, idx) => {
                    const subscriptionIdStr = sub.subscriptionId?.toString() || sub.subscriptionId;
                    const priceInfo = customerPrices.get(subscriptionIdStr);
                    const currentPrice = priceInfo?.currentPrice ?? null;
                    const found = priceInfo?.found !== false; // Default to true if not specified
                    let newPriceValue = null;
                    if (isPriceValid) {
                      if (isSetMode) {
                        newPriceValue = parsedPriceValue;
                      } else if (currentPrice !== null) {
                        newPriceValue = currentPrice + parsedPriceValue;
                      }
                    }
                    const stripeSubId =
                      priceInfo?.stripeSubscriptionId || sub.stripeSubscriptionId || subscriptionIdStr;
                    const missingStripeId = priceInfo?.missingStripeId || false;
                    const isSubIncluded = !excludedSubscriptions.has(subscriptionIdStr);

                    return (
                      <TableRow
                        key={`${customerIdStr}-${subscriptionIdStr}-${idx}`}
                        sx={{ opacity: isSubIncluded ? 1 : 0.5 }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSubIncluded}
                            onChange={() => handleToggleSubscription(sub.subscriptionId)}
                            size="small"
                          />
                        </TableCell>
                        {idx === 0 && (
                          <>
                            <TableCell rowSpan={customer.subscriptions.length}>
                              {customer.firstName} {customer.lastName}
                            </TableCell>
                            <TableCell rowSpan={customer.subscriptions.length}>
                              {customer.email}
                              {hasNoStripeCustomer && (
                                <Typography variant="caption" sx={{ display: "block", color: "error.main", mt: 0.5 }}>
                                  No Stripe customer exists
                                </Typography>
                              )}
                            </TableCell>
                          </>
                        )}
                        <TableCell>
                          {missingStripeId ? (
                            <Typography variant="body2" color="error">
                              Missing Stripe ID
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                              {stripeSubId}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {hasNoStripeCustomer ? (
                            <Typography variant="body2" color="error">
                              No price found
                            </Typography>
                          ) : loadingPrices ? (
                            <CircularProgress size={16} />
                          ) : found === false ? (
                            <Typography variant="body2" color="error">
                              Subscription not found
                            </Typography>
                          ) : currentPrice !== null ? (
                            `$${currentPrice.toFixed(2)}/month`
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {!isSubIncluded ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                              Excluded
                            </Typography>
                          ) : newPriceValue !== null ? (
                            <Typography variant="body2" fontWeight="medium">
                              ${newPriceValue.toFixed(2)}/month
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        {idx === 0 && (
                          <TableCell rowSpan={customer.subscriptions.length}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={async () => {
                                await handleSelectCustomer(customer.customerId);
                              }}
                              title="Deselect customer"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  });
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog
        open={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setDryRunResults(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Confirm Price Update &mdash; Server Preview</DialogTitle>
        <DialogContent>
          {dryRunResults ? (
            <Box sx={{ pt: 1 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This action will modify live Stripe subscriptions and cannot be undone.
              </Alert>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>{dryRunResults.summary?.totalCustomers ?? effectiveCustomerCount}</strong> customer(s),{" "}
                <strong>{dryRunResults.summary?.successCount ?? totalSelectedSubscriptions}</strong> subscription(s)
                will be updated.
                {dryRunResults.summary?.errorCount > 0 && (
                  <Typography component="span" color="error.main">
                    {" "}
                    {dryRunResults.summary.errorCount} will fail.
                  </Typography>
                )}
              </Typography>
              <TableContainer sx={{ maxHeight: 300, mt: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Subscription</TableCell>
                      <TableCell align="right">Current Price</TableCell>
                      <TableCell align="right">New Price</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dryRunResults.results?.map((result) =>
                      result.subscriptions?.map((sub, idx) => (
                        <TableRow key={`${result.customerId}-${idx}`}>
                          <TableCell>
                            {result.firstName} {result.lastName}
                          </TableCell>
                          <TableCell>{result.email}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                            {sub.stripeSubscriptionId}
                          </TableCell>
                          <TableCell align="right">${sub.oldPrice?.toFixed(2) ?? "N/A"}</TableCell>
                          <TableCell align="right">
                            <strong>${sub.newPrice?.toFixed(2) ?? "N/A"}</strong>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color={!sub.error ? "success.main" : "error.main"}>
                              {sub.error || "Ready"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowConfirmDialog(false);
              setDryRunResults(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmUpdate}
            variant="contained"
            color="error"
            disabled={!dryRunResults || dryRunResults.summary?.successCount === 0}
          >
            Confirm Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpdateSubscriptionPrices;
